import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Tooltip,
  IconButton,
  Button,
  CircularProgress,
  Paper
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL } from './../../Api/ApiUrls';
import { AuthContext } from './../../AuthContext/AuthContext';
import { Favorite, FavoriteBorder, Chat, Close, Call, LocationOn, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { CompassCalibration, SquareFoot, Person } from '@mui/icons-material';
import Explore from "@mui/icons-material/Explore";
import CropSquare from "@mui/icons-material/CropSquare";
import AccountCircle from "@mui/icons-material/AccountCircle";

// Utility function to format price in crores, lakhs, thousands
const formatPrice = (price) => {
  const num = Number(String(price).replace(/[^0-9]/g, ''));
  if (num >= 10000000) {
    return `${(num / 10000000).toFixed(2)} Cr`;
  } else if (num >= 100000) {
    return `${(num / 100000).toFixed(2)} L`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(2)} K`;
  }
  return num.toLocaleString('en-IN');
};

const ReUsableCard = ({ property, onCardClick, isSaved, toggleSave, likedCards, toggleLike, onClose, userType }) => {


  const [isSubscribed, setIsSubscribed] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [featureRequested, setFeatureRequested] = useState('');
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [userActivities, setUserActivities] = useState([]);
  const navigate = useNavigate();
  const { userId } = useContext(AuthContext);
  const [userPlan, setUserPlan] = useState(null);
  const images = property.images && property.images.length > 0
    ? property.images
    : ['/default-property-image.jpg'];

  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  useEffect(() => {
    const fetchUserPlan = async () => {
      if (!userId || !userType) {
        setUserPlan(null);
        setIsSubscribed(false);
        return;
      }
      try {
        const response = await axios.get(`${BASE_URL}/Get-user-plan/${userId}/`);
        const plans = response.data;
        const activePlan = Array.isArray(plans)
          ? plans.find(plan => plan.user_type === userType)
          : plans.user_type === userType ? plans : null;
        if (activePlan) {
          setUserPlan(activePlan);
          setIsSubscribed(true);
        } else {
          setUserPlan(null);
          setIsSubscribed(false);
        }
      } catch (error) {
        console.error('Error fetching user plan:', error);
        setUserPlan(null);
        setIsSubscribed(false);
      }
    };
    fetchUserPlan();
  }, [userId, userType]);

  useEffect(() => {
    const fetchUserActivities = async () => {
      if (!userId) return;
      try {
        const response = await fetch(`${BASE_URL}/activities/`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const allActivities = await response.json();
        const filteredActivities = allActivities.filter(activity =>
          activity && activity.user_id && String(activity.user_id) === String(userId)
        );
        setUserActivities(filteredActivities);
      } catch (error) {
        console.error('Error fetching activities:', error);
        setUserActivities([]);
      }
    };
    fetchUserActivities();
  }, [userId]);

  const checkPropertyLimit = (currentPropertyId) => {
    if (!isSubscribed) {
      setFeatureRequested("Property Access");
      setShowSubscribeModal(true);
      return false;
    }
    if (userPlan?.no_of_properties_unlimited) return true;
    const alreadyAccessed = userActivities.some(
      activity => activity.property_id === currentPropertyId && activity.activity_as === userType
    );
    if (alreadyAccessed) return true;
    const relevantActivities = userActivities.filter(activity => activity.activity_as === userType);
    const uniqueProperties = new Set(relevantActivities.map(a => a.property_id));
    const propertyLimit = userPlan?.no_of_properties || 10;
    if (uniqueProperties.size >= propertyLimit) {
      setShowLimitModal(true);
      return false;
    }
    return true;
  };

  const logActivity = async (activityType) => {
    try {
      const activityData = {
        user_id: userId,
        property_id: property.id,
        activity_type: activityType,
        property_by: property.owner,
        activity_as: userType
      };
      const response = await fetch(`${BASE_URL}/activities/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(activityData)
      });
      if (!response.ok) throw new Error('Failed to log activity');
      const newActivity = await response.json();
      setUserActivities(prev => [...prev, newActivity]);
      return true;
    } catch (err) {
      console.error("Error logging activity:", err);
      return false;
    }
  };

  const handleAction = async (e, actionName, actionCallback, activityType) => {
    e.stopPropagation();
    if (!isSubscribed) {
      setFeatureRequested(actionName);
      setShowSubscribeModal(true);
      return;
    }
    const alreadyAccessed = userActivities.some(
      activity => activity.property_id === property.id && activity.activity_as === userType
    );
    if (!alreadyAccessed) {
      const withinLimit = checkPropertyLimit(property.id);
      if (!withinLimit) return;
    }
    try {
      await actionCallback();
      if (activityType && !alreadyAccessed) await logActivity(activityType);
    } catch (error) {
      console.error(`Error in ${actionName} action:`, error);
      alert(`Failed to complete ${actionName} action`);
    }
  };

  const handleCallClick = (e) => {
    handleAction(e, "Call", async () => {
      await navigator.clipboard.writeText(property.mobile_no);
      alert(`Copied: ${property.mobile_no}`);
    }, "Call");
  };

  const handleMessageClick = (e) => {
    handleAction(e, "Message", async () => {
      navigate('/chat', { state: { property: property.id } });
    }, "Message");
  };

  const handleLikeClick = (e) => {
    handleAction(e, "Liked", () => {
      toggleLike(property.id);
    }, "Liked");
  };

  const handleLocationClick = (e) => {
    handleAction(e, "Location", async () => {
      setLoading(true);
      const openInMaps = (url) => {
        setLoading(false);
        window.open(url, '_blank', 'noopener,noreferrer');
      };
      const fallbackUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.location)}`;
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userLat = position.coords.latitude;
            const userLng = position.coords.longitude;
            const mapsUrl = property.lat && property.lng
              ? `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLng}&destination=${property.lat},${property.lng}&travelmode=driving`
              : fallbackUrl;
            openInMaps(mapsUrl);
          },
          (error) => {
            console.error("Error getting user location:", error);
            openInMaps(fallbackUrl);
          },
          { timeout: 10000 }
        );
      } else {
        openInMaps(fallbackUrl);
      }
    }, "Location");
  };

  const handleWishlistClick = async (e) => {
    e.stopPropagation();
    handleAction(e, "Wishlist", async () => {
      toggleSave(property);
    }, "Liked");
  };

  return (
    <>
  <Card
  sx={{
    display: "flex",
    flexDirection: "row",
    mb: 1,
    borderRadius: 2,
    boxShadow: 1,
    transition: "transform 0.2s ease-in-out",
    "&:hover": { transform: "scale(1.01)", boxShadow: 3 },
   height: "130px",
    width: 300,
    maxWidth: 300, 
  
    
      
  }}
 onClick={async (e) => {
    const isButtonClick = e.target.closest('button') || e.target.closest('svg');
    if (!isButtonClick && onCardClick) {
      // Check subscription first
      if (!isSubscribed) {
        setFeatureRequested("View Details");
        setShowSubscribeModal(true);
        return;
      }

      // Check if property has already been accessed
      const alreadyAccessed = userActivities.some(
        activity => activity.property_id === property.id && 
                  activity.activity_as === userType
      );

      // Check property limit (only if not already accessed)
      if (!alreadyAccessed) {
        const withinLimit = checkPropertyLimit(property.id);
        if (!withinLimit) {
          return; // Stop if limit exceeded
        }
      }

      // Log activity if it's the first interaction
      if (!alreadyAccessed) {
        try {
          await logActivity("Description");
        } catch (error) {
          console.error('Error logging description view activity:', error);
        }
      }

      onCardClick();
    }
  }}
>
 {/* Left Side: Image with Carousel */}
<Box
  sx={{
    position: "relative",
    width: "38%", height: "90%", p: 0.8
  }}
>
  <CardMedia
    component="img"
    image={images[currentImageIndex]}
    alt="Property"
    sx={{
      width: "100%",
      height: "100%",
      objectFit: "cover",
     borderRadius: 2,     // ✅ no extra radius here,
      borderTopLeftRadius: 8,
      borderBottomLeftRadius: 8,
      
    }}
  />


  {/* Carousel Navigation Arrows */}
  {images.length > 1 && (
    <>
      <IconButton
        sx={{
          position: "absolute",
          top: "50%",
          left: 4,
          transform: "translateY(-50%)",
          backgroundColor: "rgba(255,255,255,0.5)",
          "&:hover": { backgroundColor: "rgba(255,255,255,0.8)" },
        }}
        onClick={handlePrevImage}
      >
        <ChevronLeft fontSize="small" />
      </IconButton>
      <IconButton
        sx={{
          position: "absolute",
          top: "50%",
          right: 4,
          transform: "translateY(-50%)",
          backgroundColor: "rgba(255,255,255,0.5)",
          "&:hover": { backgroundColor: "rgba(255,255,255,0.8)" },
        }}
        onClick={handleNextImage}
      >
        <ChevronRight fontSize="small" />
      </IconButton>
    </>
  )}

  {/* Carousel Indicators */}
  {images.length > 1 && (
    <Box
      sx={{
        position: "absolute",
        bottom: 6,
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        gap: "4px",
      }}
    >
      {images.map((_, index) => (
        <Box
          key={index}
          onClick={(e) => {
            e.stopPropagation();
            setCurrentImageIndex(index);
          }}
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: currentImageIndex === index ? "#1976d2" : "#ccc",
            cursor: "pointer",
          }}
        />
      ))}
    </Box>
  )}

  {/* ✅ Wishlist Icon fixed to top-right */}
  <Tooltip title="Add to Wishlist">
    <IconButton
      sx={{
        position: "absolute",
        top: 7,
        right: 7,
        bgcolor: "white",
        boxShadow: 1,
        p: 0.5,
      }}
      onClick={handleWishlistClick}
    >
      {isSaved(property) ? (
        <Favorite color="error" fontSize="small" />
      ) : (
            <FavoriteBorder sx={{ fontSize: 14 }} />
      )}
    </IconButton>
  </Tooltip>
</Box>

  

  {/* Right Side: Property Data */}

<CardContent
  sx={{
    width: "65%",
    p: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  }}
>
  {/* Title + Price */}
  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
    <Typography
      fontWeight="bold"
      sx={{
        fontSize: "0.8rem",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "60%",
      }}
    >
      {property.title}-{property.property_name}
    </Typography>
    <Typography fontWeight="bold" color="primary" sx={{ fontSize: "0.8rem" }}>
      ₹{formatPrice(property.price)}
    </Typography>
  </Box>

  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
        mt: 0.5, 
    mb: 0.5,
    }}
  >
    {/* Icons */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Tooltip title="View directions in Google Maps">
        <IconButton
          size="small"
          onClick={handleLocationClick}
          sx={{ p: 0.5, color: "primary.main" }}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={14} color="inherit" />
          ) : (
            <LocationOn sx={{ fontSize: "1rem" }} />
          )}
        </IconButton>
      </Tooltip>

      <Tooltip title="Send Message">
        <IconButton
          size="small"
          onClick={handleMessageClick}
          sx={{ p: 0.5, color: "primary.main" }}
        >
          <Chat sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Call">
        <IconButton
          size="small"
          onClick={handleCallClick}
          sx={{ p: 0.5, color: "success.main" }}
        >
          <Call sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Tooltip>
    </Box>

    {/* Date */}
    <Typography color="text.secondary" sx={{ fontSize: "0.75rem" }}>
      {new Date(property.date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })}
    </Typography>
  </Box>

  {/* Property Info Grid */}
<Box
  sx={{
    display: "flex",
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    overflow: "hidden",
    minHeight: 50,
  }}
>
  {/* Facing */}
  <Box
    sx={{
      flex: 1,
      minWidth: 0, // ✅ prevents overflow
      textAlign: "center",
      p: 0.5,
      borderRight: "1px solid #e0e0e0",
    }}
  >
    <Explore sx={{ fontSize: "1rem", color: "primary.main" }} />
    <Typography
      fontWeight="bold"
      sx={{
        fontSize: "0.65rem", 
        mt: 0.2,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {property.facing || "N/A"}
    </Typography>
  </Box>

  {/* Area */}
  <Box
    sx={{
      flex: 1,
      minWidth: 0, // ✅ prevents overflow
      textAlign: "center",
      p: 0.5,
      borderRight: "1px solid #e0e0e0",
    }}
  >
    <CropSquare sx={{ fontSize: "1rem", color: "primary.main" }} />
    <Typography
      fontWeight="bold"
      sx={{
        fontSize: "0.65rem",
        mt: 0.2,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {property.length && property.width
        ? `${property.length * property.width} sqft`
        : "N/A"}
    </Typography>
  </Box>

  {/* Posted By */}
  <Box sx={{ flex: 1, minWidth: 0, textAlign: "center", p: 0.5 }}>
    <AccountCircle sx={{ fontSize: "1rem", color: "primary.main" }} />
    <Typography
      fontWeight="bold"
      sx={{
        fontSize: "0.65rem",
        mt: 0.2,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {property.listedBy || "N/A"}
    </Typography>
  </Box>
</Box>


</CardContent>

</Card>


      {showSubscribeModal && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Paper sx={{ padding: 2, maxWidth: 250, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>Subscription Required</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Please subscribe to use the {featureRequested} feature.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setShowSubscribeModal(false)}
                size="small"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setShowSubscribeModal(false);
                  navigate('/user-subscription');
                }}
                size="small"
              >
                Subscribe
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
      {showLimitModal && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Paper sx={{ padding: 2, maxWidth: 250, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>Limit Exceeded</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {userPlan?.no_of_properties_unlimited
                ? "You have unlimited access to properties."
                : `You've reached your limit of ${userPlan?.no_of_properties || 10} properties.
                 Please upgrade your subscription plan for more.`}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShowLimitModal(false)}
                size="small"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setShowLimitModal(false)}
                size="small"
              >
                OK
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </>
  );
};

export default ReUsableCard;